import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Loader2Icon,
  ShieldCheckIcon,
  ShieldOffIcon,
  ShieldAlertIcon,
  SmartphoneIcon,
  CopyIcon,
  Trash2Icon,
  MonitorIcon,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useState } from 'react'
import { toast } from 'sonner'

import { otpApi } from '../services/otpApi'

type Accent = 'blue' | 'emerald' | 'purple' | 'indigo' | 'red'

type TwoFactorSettingsProps = {
  /** Cor de destaque do portal (botões/links). */
  accent?: Accent
  /** Mostra o bloco de política da organização (somente admins). */
  showOrgEnforcement?: boolean
}

// Tailwind não compila classes interpoladas — mapear accent -> classes estáticas.
const ACCENTS: Record<Accent, { solid: string; ring: string; text: string; toggleOn: string }> = {
  blue: {
    solid: 'bg-blue-600 hover:bg-blue-700',
    ring: 'focus:ring-blue-500',
    text: 'text-blue-600',
    toggleOn: 'bg-blue-600',
  },
  emerald: {
    solid: 'bg-emerald-600 hover:bg-emerald-700',
    ring: 'focus:ring-emerald-500',
    text: 'text-emerald-600',
    toggleOn: 'bg-emerald-600',
  },
  purple: {
    solid: 'bg-purple-600 hover:bg-purple-700',
    ring: 'focus:ring-purple-500',
    text: 'text-purple-600',
    toggleOn: 'bg-purple-600',
  },
  indigo: {
    solid: 'bg-indigo-600 hover:bg-indigo-700',
    ring: 'focus:ring-indigo-500',
    text: 'text-indigo-600',
    toggleOn: 'bg-indigo-600',
  },
  red: {
    solid: 'bg-red-600 hover:bg-red-700',
    ring: 'focus:ring-red-500',
    text: 'text-red-600',
    toggleOn: 'bg-red-600',
  },
}

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

// Heurística simples: navegador + SO a partir do user-agent.
const shortenUa = (ua: string | null): string => {
  if (!ua) return 'Dispositivo desconhecido'
  const browser = /Edg/.test(ua)
    ? 'Edge'
    : /OPR|Opera/.test(ua)
      ? 'Opera'
      : /Chrome/.test(ua)
        ? 'Chrome'
        : /Firefox/.test(ua)
          ? 'Firefox'
          : /Safari/.test(ua)
            ? 'Safari'
            : 'Navegador'
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Mac OS/.test(ua)
      ? 'macOS'
      : /Android/.test(ua)
        ? 'Android'
        : /iPhone|iPad/.test(ua)
          ? 'iOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : ''
  return os ? `${browser} · ${os}` : browser
}

export function TwoFactorSettings({
  accent = 'blue',
  showOrgEnforcement = false,
}: TwoFactorSettingsProps) {
  const queryClient = useQueryClient()
  const c = ACCENTS[accent]

  // ── Dados ──
  const { data: status, isLoading } = useQuery({
    queryKey: ['otp', 'status'],
    queryFn: () => otpApi.getStatus(),
  })

  const { data: trustedDevices = [] } = useQuery({
    queryKey: ['trusted-devices'],
    queryFn: () => otpApi.listTrustedDevices(),
  })

  const { data: orgPolicy } = useQuery({
    queryKey: ['otp', 'org-policy'],
    queryFn: () => otpApi.getOrgPolicy(),
    enabled: showOrgEnforcement,
  })

  // ── Estado local ──
  const [totpSetup, setTotpSetup] = useState<{ secret: string; otpauth_uri: string } | null>(null)
  const [totpCode, setTotpCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [showTotpDisableForm, setShowTotpDisableForm] = useState(false)
  const [totpPassword, setTotpPassword] = useState('')
  const [showEmailDisableForm, setShowEmailDisableForm] = useState(false)
  const [emailPassword, setEmailPassword] = useState('')

  // ── Mutations ──
  const totpSetupMutation = useMutation({
    mutationFn: () => otpApi.totpSetup(),
    onSuccess: (data) => {
      setTotpSetup(data)
      setTotpCode('')
    },
    onError: () => toast.error('Não foi possível iniciar a configuração do app.'),
  })

  const totpEnableMutation = useMutation({
    mutationFn: () => otpApi.totpEnable({ secret: totpSetup!.secret, code: totpCode }),
    onSuccess: (data) => {
      setRecoveryCodes(data.recovery_codes)
      setTotpSetup(null)
      setTotpCode('')
      queryClient.invalidateQueries({ queryKey: ['otp', 'status'] })
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.detail || 'Código inválido. Tente novamente.'),
  })

  const totpDisableMutation = useMutation({
    mutationFn: (password: string) => otpApi.totpDisable({ password }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otp', 'status'] })
      setShowTotpDisableForm(false)
      setTotpPassword('')
      toast.success('App autenticador desativado. O login passa a usar o código por e-mail.')
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.detail || 'Não foi possível desativar.'),
  })

  const emailDisableMutation = useMutation({
    mutationFn: (password: string) => otpApi.disable({ password }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otp', 'status'] })
      setShowEmailDisableForm(false)
      setEmailPassword('')
      toast.success('Verificação por e-mail desativada.')
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.detail || 'Não foi possível desativar.'),
  })

  const revokeDeviceMutation = useMutation({
    mutationFn: (id: string) => otpApi.revokeTrustedDevice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trusted-devices'] })
      toast.success('Dispositivo revogado. No próximo login dele, o 2FA será pedido.')
    },
    onError: () => toast.error('Não foi possível revogar o dispositivo.'),
  })

  const orgPolicyMutation = useMutation({
    mutationFn: (force_otp: boolean) => otpApi.setOrgPolicy(force_otp),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['otp', 'org-policy'] })
      queryClient.invalidateQueries({ queryKey: ['otp', 'status'] })
      toast.success(
        data.force_otp
          ? '2FA agora é obrigatório para a organização.'
          : '2FA deixou de ser obrigatório para a organização.',
      )
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.detail || 'Não foi possível atualizar a política.'),
  })

  const copyRecoveryCodes = () => {
    if (recoveryCodes) {
      navigator.clipboard?.writeText(recoveryCodes.join('\n'))
      toast.success('Códigos copiados.')
    }
  }

  // O e-mail já é fallback automático quando há 2FA ativa (TOTP ou e-mail obrigatório).
  const hasBaseline = !!status?.enabled || !!status?.org_enforced || !!status?.totp_enabled
  const canDisableEmail = !!status?.enabled && !status?.totp_enabled && !status?.org_enforced

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900">Autenticação em duas etapas (2FA)</h3>
        <p className="mt-1 text-sm text-gray-500">
          {status?.org_enforced
            ? 'Sua organização exige verificação em duas etapas. O app autenticador é mais rápido e seguro que o código por e-mail, que fica como fallback.'
            : hasBaseline
              ? 'O app autenticador é mais rápido e seguro que o código por e-mail, que fica como fallback automático.'
              : 'Adicione uma camada extra de segurança. Configure o app autenticador — o código por e-mail fica como fallback.'}
        </p>
      </div>

      {isLoading || !status ? (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-4">
          <Loader2Icon className="h-5 w-5 animate-spin text-gray-400" />
          <span className="text-sm text-gray-500">Carregando...</span>
        </div>
      ) : (
        <>
          {/* ── Painel TOTP (4 estados) ── */}
          {recoveryCodes ? (
            <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 text-amber-800">
                <ShieldCheckIcon className="h-5 w-5" />
                <h4 className="font-medium">Guarde seus códigos de recuperação</h4>
              </div>
              <p className="text-sm text-amber-700">
                Use um destes códigos para entrar caso perca o acesso ao app. Cada código funciona
                uma única vez. <strong>Eles não serão mostrados novamente.</strong>
              </p>
              <div className="grid grid-cols-2 gap-2 rounded-lg border border-amber-200 bg-white p-3 font-mono text-sm">
                {recoveryCodes.map((code) => (
                  <span key={code} className="text-center text-gray-800">
                    {code}
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={copyRecoveryCodes}
                  className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm text-white hover:bg-amber-700"
                >
                  <CopyIcon className="h-4 w-4" /> Copiar códigos
                </button>
                <button
                  onClick={() => setRecoveryCodes(null)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                >
                  Entendi, guardei
                </button>
              </div>
            </div>
          ) : totpSetup ? (
            <div className="space-y-4 rounded-lg border border-gray-200 p-4">
              <div>
                <h4 className="font-medium text-gray-900">Escaneie o QR no app autenticador</h4>
                <p className="mt-1 text-sm text-gray-500">
                  Google Authenticator, Authy, 1Password… Depois digite o código de 6 dígitos.
                </p>
              </div>
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
                <div className="flex-shrink-0 rounded-lg border border-gray-200 bg-white p-3">
                  <QRCodeSVG value={totpSetup.otpauth_uri} size={160} />
                </div>
                <div className="w-full flex-1 space-y-2">
                  <p className="text-xs text-gray-500">Ou cadastre manualmente este segredo:</p>
                  <code className="block rounded bg-gray-100 px-2 py-1 text-xs break-all text-gray-800">
                    {totpSetup.secret}
                  </code>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && totpCode.length === 6) totpEnableMutation.mutate()
                    }}
                    maxLength={6}
                    placeholder="000000"
                    className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-center font-mono tracking-widest focus:ring-2 ${c.ring}`}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => totpEnableMutation.mutate()}
                      disabled={totpCode.length !== 6 || totpEnableMutation.isPending}
                      className={`rounded-lg px-4 py-2 text-sm text-white disabled:opacity-50 ${c.solid}`}
                    >
                      {totpEnableMutation.isPending ? 'Ativando...' : 'Ativar'}
                    </button>
                    <button
                      onClick={() => {
                        setTotpSetup(null)
                        setTotpCode('')
                      }}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : status.totp_enabled ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-green-100 p-2">
                    <SmartphoneIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">App autenticador ativo</h4>
                    <p className="text-sm text-gray-500">
                      Seu login pede o código do app — {status.recovery_codes_remaining ?? 0}{' '}
                      código(s) de recuperação restante(s).
                    </p>
                  </div>
                </div>
                <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                  Ativo
                </span>
              </div>

              {(status.recovery_codes_remaining ?? 0) <= 2 && (
                <p className="flex items-center gap-1.5 text-xs text-amber-700">
                  <ShieldAlertIcon className="h-3.5 w-3.5" />
                  Poucos códigos de recuperação. Reconfigure o app para gerar novos.
                </p>
              )}

              {showTotpDisableForm ? (
                <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-gray-600">
                    Digite sua senha para desativar o app autenticador:
                  </p>
                  <input
                    type="password"
                    value={totpPassword}
                    onChange={(e) => setTotpPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && totpPassword)
                        totpDisableMutation.mutate(totpPassword)
                    }}
                    placeholder="Sua senha atual"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-red-500"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => totpDisableMutation.mutate(totpPassword)}
                      disabled={!totpPassword || totpDisableMutation.isPending}
                      className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {totpDisableMutation.isPending ? 'Desativando...' : 'Confirmar desativação'}
                    </button>
                    <button
                      onClick={() => {
                        setShowTotpDisableForm(false)
                        setTotpPassword('')
                      }}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setShowTotpDisableForm(true)
                    setTotpPassword('')
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <ShieldOffIcon className="h-4 w-4" /> Desativar app autenticador
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-gray-100 p-2">
                    <SmartphoneIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">App autenticador (recomendado)</h4>
                    <p className="text-sm text-gray-500">
                      {hasBaseline
                        ? 'Hoje seu login usa o código por e-mail. Configure um app para um 2FA mais rápido e que não depende do e-mail.'
                        : 'Use um app como Google Authenticator para gerar códigos sem depender do e-mail.'}
                    </p>
                  </div>
                </div>
                <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                  Inativo
                </span>
              </div>
              <button
                onClick={() => totpSetupMutation.mutate()}
                disabled={totpSetupMutation.isPending}
                className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm text-white disabled:opacity-50 ${c.solid}`}
              >
                <SmartphoneIcon className="h-4 w-4" />
                {totpSetupMutation.isPending ? 'Carregando...' : 'Configurar app autenticador'}
              </button>
            </div>
          )}

          {/* ── Legado: desativar verificação por e-mail (opt-in antigo, sem app, sem enforcement) ── */}
          {canDisableEmail && !recoveryCodes && !totpSetup && (
            <div className="text-sm">
              {showEmailDisableForm ? (
                <div className="space-y-3 rounded-lg border border-gray-200 p-4">
                  <p className="text-gray-600">
                    Digite sua senha para desativar a verificação por e-mail:
                  </p>
                  <input
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && emailPassword)
                        emailDisableMutation.mutate(emailPassword)
                    }}
                    placeholder="Sua senha atual"
                    className={`w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 ${c.ring}`}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => emailDisableMutation.mutate(emailPassword)}
                      disabled={!emailPassword || emailDisableMutation.isPending}
                      className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {emailDisableMutation.isPending ? 'Desativando...' : 'Confirmar'}
                    </button>
                    <button
                      onClick={() => {
                        setShowEmailDisableForm(false)
                        setEmailPassword('')
                      }}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">
                  Verificação por e-mail ativa.{' '}
                  <button
                    onClick={() => {
                      setShowEmailDisableForm(true)
                      setEmailPassword('')
                    }}
                    className={`underline ${c.text} hover:opacity-80`}
                  >
                    Desativar verificação por e-mail
                  </button>
                </p>
              )}
            </div>
          )}

          {/* ── Dispositivos confiáveis ── */}
          <div>
            <h4 className="mb-2 text-sm font-medium text-gray-900">Dispositivos confiáveis</h4>
            <p className="mb-3 text-xs text-gray-500">
              Navegadores onde você marcou "lembrar" pulam o 2FA por um período. Revogue os que não
              reconhecer.
            </p>
            {trustedDevices.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-400">
                Nenhum dispositivo confiável.
              </p>
            ) : (
              <div className="space-y-2">
                {trustedDevices.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 p-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <MonitorIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                      <div className="min-w-0">
                        <p className="truncate text-sm text-gray-800">
                          {shortenUa(d.user_agent)}
                          {d.current && (
                            <span className="ml-2 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-600/20 ring-inset">
                              este dispositivo
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {d.ip || '—'}
                          {d.last_used_at ? ` · usado em ${formatDate(d.last_used_at)}` : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => revokeDeviceMutation.mutate(d.id)}
                      disabled={revokeDeviceMutation.isPending}
                      className="flex-shrink-0 rounded-lg p-2 text-red-500 hover:bg-red-50"
                      title="Revogar"
                    >
                      <Trash2Icon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Política da organização (admins) ── */}
          {showOrgEnforcement && orgPolicy && (
            <div className="border-t border-gray-100 pt-2">
              <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex-shrink-0 rounded-full bg-gray-100 p-2">
                    <ShieldCheckIcon className="h-5 w-5 text-gray-500" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-medium text-gray-900">Exigir 2FA na organização</h4>
                    <p className="text-sm text-gray-500">
                      Obriga todos os usuários da organização a usar verificação em duas etapas.
                    </p>
                  </div>
                </div>
                <button
                  role="switch"
                  aria-checked={orgPolicy.force_otp}
                  disabled={orgPolicyMutation.isPending}
                  onClick={() => orgPolicyMutation.mutate(!orgPolicy.force_otp)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                    orgPolicy.force_otp ? c.toggleOn : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      orgPolicy.force_otp ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default TwoFactorSettings
